import React, { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { useGetOrders, usePostCartImportFromOrder } from 'api';

import OrderPositionsTooltip from 'components/wise/WiseCartImportModal/components/OrderPositionsTooltip';
import styles from 'theme/components/wise/WiseCartImportModal/WiseCartImportModal.module.scss';

export type CartImportOrdersTabProps = {
  cartId: number;
  onSuccess: () => void;
};

const PAGE_LIMIT = 10;

const CartImportOrdersTab: FC<CartImportOrdersTabProps> = ({ cartId, onSuccess }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [submittingOrderId, setSubmittingOrderId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data, isFetching } = useGetOrders(
    { page, limit: PAGE_LIMIT },
    { keepPreviousData: true }
  );

  const mutation = usePostCartImportFromOrder(cartId, {
    onSuccess: () => {
      setSubmittingOrderId(null);
      onSuccess();
    },
    onError: () => {
      setSubmittingOrderId(null);
      setErrorMessage(t('Nie udalo sie dopisac pozycji zamowienia'));
    },
  });

  const handleOrderClick = (orderId: number) => {
    setErrorMessage(null);
    setSubmittingOrderId(orderId);
    mutation.mutate({ orderId });
  };

  const orders = data?.items ?? [];
  const totalPages = data?.total_pages ?? 0;

  return (
    <div className={styles.modalBody}>
      {isFetching && orders.length === 0 && (
        <div className={styles.loading}>{t('Ladowanie zamowien...')}</div>
      )}

      {orders.map((order) => {
        const isSubmitting = submittingOrderId === order.id;
        return (
          <OrderPositionsTooltip key={order.id} orderId={order.id}>
            <div
              className={classnames(styles.orderRow, {
                [styles.actionButton]: false,
              })}
              role="button"
              tabIndex={0}
              onClick={() => !isSubmitting && handleOrderClick(order.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  if (!isSubmitting) handleOrderClick(order.id);
                }
              }}
              aria-disabled={isSubmitting}>
              <div>
                <div className={styles.orderSymbol}>{order.symbol || `#${order.id}`}</div>
                <div className={styles.orderMeta}>
                  {order.create_date} · {order.products_total_count}{' '}
                  {t('produktow')} · {order.value_net_formatted} {order.currency}
                </div>
              </div>
              <span className={classnames(styles.badge, styles.badgeNew)}>
                {isSubmitting ? t('Dodawanie...') : t('Dopisz do koszyka')}
              </span>
            </div>
          </OrderPositionsTooltip>
        );
      })}

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || isFetching}>
            {t('Poprzednia')}
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || isFetching}>
            {t('Nastepna')}
          </button>
        </div>
      )}

      {errorMessage && <div className={styles.invalidTitle}>{errorMessage}</div>}
    </div>
  );
};

export default CartImportOrdersTab;
